/**
 * ClubMembers.jsx — dark theme cohérent avec le reste du dashboard
 */
import { useState, useEffect } from 'react'
import { UserPlus, UserMinus, RotateCcw, Edit2, X, Mail } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import { useAuth } from '../context/AuthContext'
import { useCategoriesOptions } from '../constants/categories'

// L'URL etait codee en dur · cette page tapait la PRODUCTION meme en local,
// donc un test de suppression sur localhost retirait un vrai membre du vrai
// club. Aligne sur le motif des autres pages (MEMBRE-DELETE-001).
const API = `${import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'}/club/members`

const G = {
  bg: '#F7F7F7', bg2: '#FFFFFF',
  gold: '#4F8EF7', goldD: '#D4900A',
  goldBg: '#FFFBEB', goldBdr: 'rgba(79,142,247,0.25)',
  mono: "'Space Grotesk', sans-serif", display: "'Barlow Condensed', sans-serif",
  border: '#F0F0F0', muted: '#888888',
  text: '#0A0A0A', green: '#3DBF7A', red: '#D32F2F', blue: '#4F8EF7', orange: '#4F8EF7',
}

const ROLES = ['admin', 'coach', 'analyst']
const ROLE_LABELS = { admin: 'Administrateur', coach: 'Coach', analyst: 'Analyste vidéo' }
const ROLE_COLORS = { admin: G.gold, coach: G.blue, analyst: '#8b5cf6' }
// LES CLES SONT EN MAJUSCULES, comme l'enum InviteStatus du backend, qui
// serialise `status.value` — donc 'ACCEPTED', 'PENDING', 'DECLINED'.
//
// Elles etaient en minuscules. Trois consequences visibles le meme jour · les
// compteurs affichaient (0) quoi qu'il arrive, les onglets de filtre rendaient
// une liste vide, et le badge retombait sur la valeur brute — un coach voyait
// « DECLINED » en gris au lieu de « Retire ».
//
// « Retire » et non « Refuse » · DECLINED couvre deux gestes distincts, une
// invitation declinee par l'invite et un retrait decide par le DS. Le second
// est le cas courant, et c'est celui que le DS lit dans cette liste.
const STATUS_COLORS = { ACCEPTED: G.green, PENDING: G.orange, DECLINED: G.red }
const STATUS_LABELS = { ACCEPTED: 'Actif', PENDING: 'En attente', DECLINED: 'Retiré' }
// La liste des catégories vient désormais du hook SSoT (BUG-012, retire U20
// hors référentiel). Consommée via useCategoriesOptions() dans InviteModal
// et EditModal ci-dessous.

function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

function safeJson(res) {
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) return Promise.resolve(null)
  return res.json()
}

function RoleBadge({ role }) {
  const color = ROLE_COLORS[role] || G.muted
  return <span style={{ fontFamily: G.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', padding: '3px 8px', background: color + '15', color, border: `1px solid ${color}25`, borderRadius: 20 }}>{ROLE_LABELS[role] || role}</span>
}

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || G.muted
  return <span style={{ fontFamily: G.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', padding: '3px 8px', background: color + '15', color, border: `1px solid ${color}25`, borderRadius: 20 }}>{STATUS_LABELS[status] || status}</span>
}

function Avatar({ name }) {
  const initials = name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?'
  return (
    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F0F0F0', border: `1px solid #EBEBEB`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontFamily: G.display, fontSize: 13, fontWeight: 700, color: '#0A0A0A' }}>{initials}</span>
    </div>
  )
}

const inputStyle = {
  width: '100%', background: '#FFFFFF', border: `0.5px solid #EBEBEB`,
  padding: '11px 14px', color: G.text, fontFamily: G.mono, fontSize: 13,
  outline: 'none', boxSizing: 'border-box', transition: 'border-color .15s',
  borderRadius: 3, minHeight: 44,
}
const labelStyle = { fontFamily: G.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: '#888888', marginBottom: 6, display: 'block' }

function InviteModal({ onClose, onSuccess }) {
  const { categories } = useCategoriesOptions()
  const [form, setForm] = useState({ email: '', role: 'coach', category: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    setError('')
    if (!form.email) return setError('Email requis')
    if (form.role === 'coach' && !form.category) return setError('Catégorie requise pour un Coach')
    setLoading(true)
    try {
      const res = await fetch(`${API}/invite`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ email: form.email, role: form.role.toUpperCase(), category: form.role === 'coach' ? form.category : null })
      })
      const data = await safeJson(res)
      if (!res.ok) throw new Error(data ? (typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail)) : 'Erreur serveur')
      onSuccess(); onClose()
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: '#FFFFFF', width: '100%', maxWidth: 440, border: `0.5px solid #EBEBEB`, borderTop: `2.5px solid #0A0A0A`, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
        <div style={{ padding: '20px 24px', borderBottom: `0.5px solid #EBEBEB`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: G.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: G.muted, marginBottom: 4 }}>Nouveau membre</div>
            <div style={{ fontFamily: G.display, fontSize: 22, fontWeight: 700, textTransform: 'uppercase', color: G.text }}>Inviter</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color={G.muted} /></button>
        </div>
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <div style={{ padding: '10px 14px', background: '#FFF0F0', border: `1px solid rgba(211,47,47,0.2)`, fontFamily: G.mono, fontSize: 12, color: '#D32F2F', borderRadius: 3 }}>{error}</div>}
          <div><label style={labelStyle}>Email</label>
            <input type="email" placeholder="coach@monclub.fr" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#0A0A0A'} onBlur={e => e.target.style.borderColor = '#EBEBEB'} /></div>
          <div><label style={labelStyle}>Rôle</label>
            <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value, category: '' }))} style={{ ...inputStyle, cursor: 'pointer' }}>
              {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select></div>
          {form.role === 'coach' && (
            <div><label style={labelStyle}>Catégorie</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">Sélectionner</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
          )}
          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '11px', background: 'transparent', border: `0.5px solid #EBEBEB`, fontFamily: G.mono, fontSize: 11, fontWeight: 500, color: G.muted, cursor: 'pointer', borderRadius: 3, minHeight: 44 }}>Annuler</button>
            <button onClick={handleSubmit} disabled={loading} style={{ flex: 2, padding: '11px', background: loading ? '#888' : '#0A0A0A', border: 'none', fontFamily: G.mono, fontSize: 11, fontWeight: 600, color: '#FFFFFF', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 3, minHeight: 44 }}>
              {loading ? 'Envoi...' : <><Mail size={13} /> Envoyer l'invitation</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function EditModal({ member, onClose, onSuccess }) {
  const { categories } = useCategoriesOptions()
  const [form, setForm] = useState({ role: member.role, category: member.category || '' })
  const [loading, setLoading] = useState(false)

  const handleSave = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API}/${member.id}`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ role: form.role.toUpperCase(), category: form.role === 'coach' ? form.category : null })
      })
      if (!res.ok) {
        const data = await safeJson(res)
        alert(data?.detail || 'Erreur lors de la mise à jour'); return
      }
      onSuccess(); onClose()
    } catch { alert('Erreur lors de la mise à jour') }
    finally { setLoading(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: G.bg2, width: '100%', maxWidth: 380, border: `1px solid ${G.border}`, borderTop: `2px solid ${G.gold}` }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: G.display, fontSize: 20, textTransform: 'uppercase', color: G.text }}>Modifier</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} color={G.muted} /></button>
        </div>
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text }}>{member.user_name || member.email}</div>
          <div><label style={labelStyle}>Rôle</label>
            <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
              {ROLES.map(r => <option key={r} value={r} style={{ background: G.bg2 }}>{ROLE_LABELS[r]}</option>)}
            </select></div>
          {form.role === 'coach' && (
            <div><label style={labelStyle}>Catégorie</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="" style={{ background: G.bg2 }}>Sélectionner</option>
                {categories.map(c => <option key={c} value={c} style={{ background: G.bg2 }}>{c}</option>)}
              </select></div>
          )}
          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'transparent', border: `0.5px solid #EBEBEB`, fontFamily: G.mono, fontSize: 11, fontWeight: 500, color: G.muted, cursor: 'pointer', borderRadius: 3, minHeight: 44 }}>Annuler</button>
            <button onClick={handleSave} disabled={loading} style={{ flex: 2, padding: '10px', background: '#0A0A0A', border: 'none', fontFamily: G.mono, fontSize: 11, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', borderRadius: 3, minHeight: 44 }}>
              {loading ? 'Sauvegarde...' : 'Confirmer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ClubMembers() {
  const { user } = useAuth()
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showInvite, setShowInvite] = useState(false)
  const [editMember, setEditMember] = useState(null)
  const [filter, setFilter] = useState('all')

  // Guard : seul l'admin peut voir cette page
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'admin'

  useEffect(() => { if (isAdmin) loadMembers(); else setLoading(false) }, [isAdmin])

  const loadMembers = async () => {
    setLoading(true)
    try {
      const res = await fetch(API, { headers: authHeaders() })
      const ct = res.headers.get('content-type') || ''
      if (!ct.includes('application/json')) { setMembers([]); return }
      const data = await res.json()
      setMembers(Array.isArray(data) ? data : [])
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  // LE CONFIRM DIT CE QUE LE RETRAIT FAIT VRAIMENT.
  //
  // Il demandait « Retirer X du club ? » et l'icone etait une corbeille · deux
  // promesses fausses. Rien n'est supprime — le ClubMember passe en DECLINED
  // et tout l'historique est conserve — mais le coach perd l'ecriture LE JOUR
  // MEME, `remove_member` appelant `expire_free_now`. Un DS a retire un coach
  // en croyant ne rien faire, et l'a bloque en quatre minutes.
  const handleRemove = async (member) => {
    const qui = member.user_name || member.email
    const message =
      `Retirer ${qui} du club ?\n\n` +
      `Il gardera l'accès en lecture à son historique, mais perdra ` +
      `immédiatement la possibilité de créer des matchs, saisir des ` +
      `compositions ou lancer des analyses.\n\n` +
      `Tu pourras le réintégrer à tout moment.`
    if (!window.confirm(message)) return
    try {
      const res = await fetch(`${API}/${member.id}`, { method: 'DELETE', headers: authHeaders() })
      if (!res.ok) {
        const data = await safeJson(res)
        alert(data?.detail || 'Erreur lors du retrait'); return
      }
      // On RECHARGE au lieu de retirer la ligne localement · le membre n'est
      // pas supprime, il change de statut. Le filtrer a l'ecran donnait
      // l'illusion d'une suppression, et il reapparaissait au rechargement
      // suivant sans que rien n'explique pourquoi.
      await loadMembers()
    } catch { alert('Erreur lors du retrait') }
  }

  const handleReintegrate = async (member) => {
    const qui = member.user_name || member.email
    if (!window.confirm(`Réintégrer ${qui} dans le club ?\n\nIl retrouvera immédiatement l'accès complet, couvert par l'abonnement du club.`)) return
    try {
      const res = await fetch(`${API}/${member.id}/reintegrer`, { method: 'POST', headers: authHeaders() })
      if (!res.ok) {
        const data = await safeJson(res)
        alert(data?.detail || 'Erreur lors de la réintégration'); return
      }
      await loadMembers()
    } catch { alert('Erreur lors de la réintégration') }
  }

  const filtered = members.filter(m => filter === 'all' || m.status === filter)
  const counts = {
    all: members.length,
    ACCEPTED: members.filter(m => m.status === 'ACCEPTED').length,
    PENDING:  members.filter(m => m.status === 'PENDING').length,
    DECLINED: members.filter(m => m.status === 'DECLINED').length,
  }

  const thStyle = { fontFamily: G.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: G.muted, padding: '10px 16px', textAlign: 'left', borderBottom: `0.5px solid ${G.border}`, whiteSpace: 'nowrap', background: '#FAFAFA' }
  const tdStyle = { fontFamily: G.mono, fontSize: 12, fontWeight: 400, color: G.text, padding: '13px 16px', borderBottom: `0.5px solid ${G.border}`, verticalAlign: 'middle' }

  // Accès refusé pour les non-admins
  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: 16 }}>
          <div style={{ fontFamily: G.display, fontSize: 36, fontWeight: 700, textTransform: 'uppercase', color: '#EBEBEB' }}>Accès refusé</div>
          <div style={{ fontFamily: G.mono, fontSize: 13, color: G.muted }}>Cette page est réservée aux administrateurs du club.</div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: `0.5px solid #EBEBEB` }}>
        <p style={{ fontFamily: G.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: G.muted, marginBottom: 8 }}>
          Gestion accès
        </p>
        <h1 style={{ fontFamily: G.display, fontSize: 36, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-.01em', color: G.text, margin: 0 }}>
          Membres du club
        </h1>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 0, borderBottom: `0.5px solid ${G.border}` }}>
          {/* Un onglet « Retires » · sans lui, l'etat DECLINED existait en base
              sans aucune facon de le trier a l'ecran. */}
          {[{ key: 'all', label: 'Tous' }, { key: 'ACCEPTED', label: 'Actifs' }, { key: 'PENDING', label: 'En attente' }, { key: 'DECLINED', label: 'Retirés' }].map(({ key, label }) => (
            <button key={key} onClick={() => setFilter(key)} style={{
              padding: '8px 16px', background: 'transparent',
              border: 'none', borderBottom: `2px solid ${filter === key ? '#0A0A0A' : 'transparent'}`,
              fontFamily: G.mono, fontSize: 11, fontWeight: filter === key ? 600 : 500,
              color: filter === key ? G.text : G.muted,
              cursor: 'pointer', transition: 'all .12s',
            }}>
              {label} <span style={{ opacity: .6 }}>({counts[key]})</span>
            </button>
          ))}
        </div>
        <button onClick={() => setShowInvite(true)} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px',
          background: '#0A0A0A', border: 'none', fontFamily: G.mono, fontSize: 11,
          fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', borderRadius: 3, minHeight: 44,
        }}>
          <UserPlus size={14} /> Inviter un membre
        </button>
      </div>

      <div style={{ background: G.bg2, border: `0.5px solid ${G.border}`, overflowX: 'auto', borderRadius: 3 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', fontFamily: G.mono, fontSize: 12, color: G.muted }}>Chargement...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 24px' }}>
            <div style={{ fontFamily: G.display, fontSize: 28, fontWeight: 700, textTransform: 'uppercase', color: '#EBEBEB', marginBottom: 12 }}>Aucun membre</div>
            <div style={{ fontFamily: G.mono, fontSize: 13, color: G.muted, marginBottom: 20 }}>
              {filter === 'PENDING' ? 'Aucune invitation en attente' : filter === 'DECLINED' ? 'Aucun membre retiré' : 'Invitez votre premier membre'}
            </div>
            {filter === 'all' && (
              <button onClick={() => setShowInvite(true)} style={{ padding: '10px 20px', background: '#0A0A0A', border: 'none', fontFamily: G.mono, fontSize: 11, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', borderRadius: 3, minHeight: 44 }}>
                Inviter un membre
              </button>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Membre', 'Rôle', 'Catégorie', 'Statut', 'Invité le', 'Actions'].map(h => <th key={h} style={h === 'Actions' ? { ...thStyle, textAlign: 'right' } : thStyle}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map(member => (
                <tr key={member.id}
                  onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                  onMouseLeave={e => e.currentTarget.style.background = member.status === 'DECLINED' ? '#FBFBFB' : 'transparent'}
                  style={{
                    transition: 'background .1s',
                    // Un membre retire reste VISIBLE · le masquer recreerait le
                    // defaut d'origine, un etat en base sans representation a
                    // l'ecran. Il est grise, pas cache.
                    background: member.status === 'DECLINED' ? '#FBFBFB' : 'transparent',
                    opacity: member.status === 'DECLINED' ? 0.62 : 1,
                  }}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Avatar name={member.user_name || member.email} />
                      <div>
                        <div style={{ fontFamily: G.mono, fontSize: 13, fontWeight: 600, color: G.text }}>{member.user_name || '—'}</div>
                        <div style={{ fontFamily: G.mono, fontSize: 11, color: G.muted, marginTop: 2 }}>{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}><RoleBadge role={member.role} /></td>
                  <td style={tdStyle}><span style={{ fontFamily: G.mono, fontSize: 12, color: member.category ? G.text : G.muted }}>{member.category || '—'}</span></td>
                  <td style={tdStyle}><StatusBadge status={member.status} /></td>
                  <td style={tdStyle}><span style={{ fontFamily: G.mono, fontSize: 11, color: G.muted }}>{new Date(member.invited_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span></td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                      <button onClick={() => setEditMember(member)} style={{ padding: '5px 10px', background: 'transparent', border: `0.5px solid ${G.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: G.mono, fontSize: 10, fontWeight: 500, color: G.muted, transition: 'all .12s', borderRadius: 3 }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#0A0A0A'; e.currentTarget.style.color = '#0A0A0A' }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.color = G.muted }}>
                        <Edit2 size={11} /> Modifier
                      </button>
                      {member.status === 'DECLINED' ? (
                        <button onClick={() => handleReintegrate(member)} style={{ padding: '5px 10px', background: '#F0FBF3', border: `1px solid ${G.green}35`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: G.mono, fontSize: 10, fontWeight: 600, color: G.green, transition: 'all .12s', borderRadius: 3 }}
                          onMouseEnter={e => e.currentTarget.style.background = '#E3F6E9'}
                          onMouseLeave={e => e.currentTarget.style.background = '#F0FBF3'}>
                          <RotateCcw size={11} /> Réintégrer
                        </button>
                      ) : (
                        /* LIBELLE, PAS UNE CORBEILLE SEULE · une corbeille
                           promet une suppression, or rien n'est supprime. */
                        <button onClick={() => handleRemove(member)} style={{ padding: '5px 10px', background: '#FFF0F0', border: `1px solid rgba(211,47,47,0.2)`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: G.mono, fontSize: 10, fontWeight: 500, color: G.red, transition: 'all .12s', borderRadius: 3 }}
                          onMouseEnter={e => e.currentTarget.style.background = '#FFE0E0'}
                          onMouseLeave={e => e.currentTarget.style.background = '#FFF0F0'}>
                          <UserMinus size={11} /> Retirer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onSuccess={loadMembers} />}
      {editMember && <EditModal member={editMember} onClose={() => setEditMember(null)} onSuccess={loadMembers} />}
    </DashboardLayout>
  )
}
